/** Is Waki Mail reachable? (2026-10-06)
 *
 *  Without the internet the app used to fail piece by piece: lists that
 *  would not load, "request failed" notes, and, on a fresh start, the
 *  sign-in page, as if the session had ended. Now one watcher sees every
 *  same-origin request: a request that fails at the network level (or the
 *  browser saying it is offline) starts a check of /auth/me; while the check
 *  fails the app says so (ConnectionBanner, AuthGate's offline screen) and
 *  keeps checking, a little less often each time; when it answers, the app
 *  says "Back online" and every view re-reads ("wov:reconnected").
 *
 *  "offline": the device has no network. "unreachable": it has, but Waki
 *  Mail's server does not answer (the Mac at home asleep, the tunnel down).
 *  A failing Outlook bridge is neither: the daemon answers for it. */

export type Connection = "online" | "offline" | "unreachable";

const DELAYS_MS = [2_000, 4_000, 8_000, 15_000, 30_000];

let state: Connection = typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "online";
const listeners = new Set<(c: Connection) => void>();
let rawFetch: typeof fetch | null = null;
let probeTimer: ReturnType<typeof setTimeout> | undefined;
let probing = false;
let attempt = 0;

export function getConnection(): Connection {
  return state;
}

export function subscribeConnection(fn: (c: Connection) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function set(next: Connection): void {
  if (next === state) return;
  const was = state;
  state = next;
  for (const l of Array.from(listeners)) l(next);
  if (next === "online") {
    attempt = 0;
    if (probeTimer) clearTimeout(probeTimer);
    probeTimer = undefined;
    if (was !== "online") window.dispatchEvent(new Event("wov:reconnected"));
  } else {
    schedule();
  }
}

/** A Cloudflare error page (the server behind it did not answer), as
 *  opposed to an answer from the daemon. */
export function isEdgeFailure(status: number, contentType: string): boolean {
  return (status === 530 || (status >= 502 && status <= 504) || (status >= 520 && status <= 529)) && !contentType.includes("json");
}

function schedule(): void {
  if (probeTimer || state === "online") return;
  const delay = DELAYS_MS[Math.min(attempt, DELAYS_MS.length - 1)];
  attempt++;
  probeTimer = setTimeout(() => {
    probeTimer = undefined;
    void probe();
  }, delay);
}

/** Check now ("Try again"). Resolves to the state after the check. */
export async function probe(): Promise<Connection> {
  if (probing) return state;
  probing = true;
  try {
    const f = rawFetch ?? fetch;
    const r = await f("/auth/me", { credentials: "same-origin", cache: "no-store", signal: AbortSignal.timeout(8_000) });
    if (isEdgeFailure(r.status, r.headers.get("content-type") ?? "")) set("unreachable");
    else set("online");
  } catch {
    set(navigator.onLine === false ? "offline" : "unreachable");
  } finally {
    probing = false;
  }
  if (state !== "online") schedule();
  return state;
}

function sameOrigin(input: RequestInfo | URL): boolean {
  try {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    return new URL(url, location.href).origin === location.origin;
  } catch {
    return false;
  }
}

function isAbort(e: unknown, init?: RequestInit): boolean {
  return (e instanceof DOMException && (e.name === "AbortError" || e.name === "TimeoutError")) || !!init?.signal?.aborted;
}

/** Once, at start-up (main.tsx). */
export function installConnectionWatch(): void {
  if (rawFetch || typeof window === "undefined") return;
  rawFetch = window.fetch.bind(window);
  const original = rawFetch;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const r = await original(input, init);
      if (sameOrigin(input)) {
        if (isEdgeFailure(r.status, r.headers.get("content-type") ?? "")) {
          if (state === "online") void probe();
        } else if (state !== "online") {
          set("online");
        }
      }
      return r;
    } catch (e) {
      if (sameOrigin(input) && !isAbort(e, init) && state === "online") void probe();
      throw e;
    }
  };
  window.addEventListener("offline", () => set("offline"));
  window.addEventListener("online", () => void probe());
  // Back to the app after a while away: check rather than wait for the timer.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && state !== "online") void probe();
  });
  if (state !== "online") schedule();
}
